import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../../context/AdminContext';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Shield, FileText, Clock, CheckCircle, XCircle, 
  AlertCircle, Package 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminWarranty = () => {
  const { getAuthHeaders } = useAdmin();
  const [warranties, setWarranties] = useState([]);
  const [claims, setClaims] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('claims');
  
  // Decision form
  const [decidingId, setDecidingId] = useState(null);
  const [decisionForm, setDecisionForm] = useState({
    resolution: 'approved',
    admin_notes: '',
    refund_amount: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [claimsRes, warrantiesRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/admin/warranty/claims`, { headers }),
        axios.get(`${API}/admin/warranty/list`, { headers }),
        axios.get(`${API}/admin/warranty/analytics`, { headers })
      ]);
      
      setClaims(claimsRes.data.claims || []);
      setWarranties(warrantiesRes.data.warranties || []);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching warranty data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDecision = async (claimId) => {
    setSubmitting(true);
    try {
      await axios.put(`${API}/admin/warranty/claims/${claimId}/decision`, {
        resolution: decisionForm.resolution,
        admin_notes: decisionForm.admin_notes,
        refund_amount: decisionForm.refund_amount ? parseFloat(decisionForm.refund_amount) : null
      }, { headers: getAuthHeaders() });
      
      setDecidingId(null);
      setDecisionForm({ resolution: 'approved', admin_notes: '', refund_amount: '' });
      fetchData();
    } catch (error) {
      console.error('Error processing decision:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      submitted: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      denied: 'bg-red-100 text-red-800',
      partial: 'bg-blue-100 text-blue-800',
      active: 'bg-green-100 text-green-800',
      expired: 'bg-gray-100 text-gray-800',
      voided: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-stone-100 text-stone-800';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 space-y-6" data-testid="admin-warranty">
        <h1 className="text-2xl font-bold text-stone-800">Warranty Management</h1>

        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Shield className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-800">{analytics?.total_warranties || 0}</p>
                  <p className="text-stone-500 text-sm">Warranties</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-100 rounded-full">
                  <FileText className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-800">{analytics?.total_claims || 0}</p>
                  <p className="text-stone-500 text-sm">Claims</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-800">
                    {analytics?.claims_by_status?.approved || 0}
                  </p>
                  <p className="text-stone-500 text-sm">Approved</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-full">
                  <Clock className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-800">
                    {analytics?.avg_resolution_hours?.toFixed(0) || 0}h
                  </p>
                  <p className="text-stone-500 text-sm">Avg Resolution</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="claims">
              Claims ({claims.length})
            </TabsTrigger>
            <TabsTrigger value="warranties">
              All Warranties ({warranties.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="claims" className="space-y-4">
            {claims.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <FileText className="h-16 w-16 mx-auto mb-4 text-stone-300" />
                  <h3 className="text-lg font-medium text-stone-800 mb-2">No claims</h3>
                  <p className="text-stone-500">No warranty claims have been filed</p>
                </CardContent>
              </Card>
            ) : (
              claims.map((claim) => (
                <Card key={claim.id}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="font-semibold text-stone-800">{claim.claim_number}</p>
                        <p className="text-sm text-stone-500">Warranty: {claim.warranty_number}</p>
                        <p className="text-sm text-stone-500">{claim.customer_email}</p>
                        <p className="text-sm text-stone-500 flex items-center gap-1 mt-1">
                          <Clock className="h-4 w-4" />
                          {new Date(claim.created_at).toLocaleString()}
                        </p>
                      </div>
                      <Badge className={getStatusColor(claim.status)}>
                        {claim.status}
                      </Badge>
                    </div>

                    <div className="bg-stone-50 rounded-lg p-3 mb-4">
                      <p className="text-sm font-medium text-stone-700 mb-2">Issue Description:</p>
                      <p className="text-sm text-stone-600">{claim.issue_description}</p>
                    </div>

                    {claim.products && claim.products.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-medium text-stone-700 mb-2">Products:</p>
                        {claim.products.map((product, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-stone-600">
                            <Package className="h-4 w-4" />
                            {product.product_name}
                          </div>
                        ))}
                      </div>
                    )}

                    {claim.status === 'submitted' && (
                      decidingId === claim.id ? (
                        <div className="space-y-4 border-t pt-4">
                          <div className="space-y-2">
                            <Label>Decision</Label>
                            <Select
                              value={decisionForm.resolution}
                              onValueChange={(v) => setDecisionForm(prev => ({ ...prev, resolution: v }))}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="approved">Approve - Full Refund/Replacement</SelectItem>
                                <SelectItem value="partial">Partial - Partial Refund</SelectItem>
                                <SelectItem value="denied">Deny</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {decisionForm.resolution !== 'denied' && (
                            <div className="space-y-2">
                              <Label>Refund Amount (optional)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                value={decisionForm.refund_amount}
                                onChange={(e) => setDecisionForm(prev => ({ ...prev, refund_amount: e.target.value }))}
                              />
                            </div>
                          )}
                          <div className="space-y-2">
                            <Label>Notes</Label>
                            <Textarea
                              value={decisionForm.admin_notes}
                              onChange={(e) => setDecisionForm(prev => ({ ...prev, admin_notes: e.target.value }))}
                              placeholder="Add notes about your decision..."
                              rows={2}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleDecision(claim.id)}
                              className="bg-amber-600 hover:bg-amber-700"
                              disabled={submitting}
                            >
                              Submit Decision
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => setDecidingId(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button
                          onClick={() => setDecidingId(claim.id)}
                          className="bg-amber-600 hover:bg-amber-700"
                        >
                          Process Claim
                        </Button>
                      )
                    )}

                    {claim.admin_notes && (
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm font-medium text-blue-800">Resolution Notes:</p>
                        <p className="text-sm text-blue-700">{claim.admin_notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="warranties" className="space-y-4">
            {warranties.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Shield className="h-16 w-16 mx-auto mb-4 text-stone-300" />
                  <h3 className="text-lg font-medium text-stone-800 mb-2">No warranties</h3>
                  <p className="text-stone-500">No warranties have been registered</p>
                </CardContent>
              </Card>
            ) : (
              warranties.map((warranty) => (
                <Card key={warranty.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-stone-800">{warranty.warranty_number}</p>
                        <p className="text-sm text-stone-500">Order: {warranty.order_number}</p>
                        <p className="text-sm text-stone-500">{warranty.customer_email}</p>
                      </div>
                      <div className="text-right">
                        <Badge className={getStatusColor(warranty.status)}>
                          {warranty.status}
                        </Badge>
                        <p className="text-xs text-stone-500 mt-1">
                          Expires: {new Date(warranty.expiration_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminWarranty;
